import React, { useEffect, useCallback } from 'react'
import { getOptimizedImageUrl } from '../lib/image'

export default function Lightbox({
  images = [],
  currentIndex = 0,
  isOpen = false,
  onClose,
  onNext,
  onPrev,
  title = '',
}) {
  const handleKeyDown = useCallback(
    (e) => {
      if (!isOpen) return
      if (e.key === 'Escape') onClose?.()
      if (e.key === 'ArrowRight') onNext?.()
      if (e.key === 'ArrowLeft') onPrev?.()
    },
    [isOpen, onClose, onNext, onPrev]
  )

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
      window.addEventListener('keydown', handleKeyDown)
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, handleKeyDown])

  if (!isOpen || images.length === 0) return null

  const currentImage = images[currentIndex] || images[0]

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Billedvisning"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 backdrop-blur-md transition-opacity animate-in fade-in duration-200"
      onClick={onClose}
    >
      {/* Top bar controls */}
      <div
        className="absolute top-0 inset-x-0 z-10 flex items-center justify-between px-6 py-4 bg-gradient-to-b from-black/80 to-transparent text-white"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3">
          <span className="font-mono text-sm px-3 py-1 rounded-full bg-white/10 text-white font-medium border border-white/20">
            {currentIndex + 1} / {images.length}
          </span>
          {title && (
            <span className="text-sm font-medium text-slate-300 hidden sm:inline-block">
              {title}
            </span>
          )}
        </div>

        <button
          type="button"
          onClick={onClose}
          aria-label="Luk visning"
          className="flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-red-600 hover:text-white"
        >
          <i className="fa-solid fa-xmark text-lg" aria-hidden="true" />
        </button>
      </div>

      {/* Main Image Container */}
      <div
        className="relative max-h-[85vh] max-w-[90vw] flex items-center justify-center select-none"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={getOptimizedImageUrl(currentImage, { width: 1600, quality: 90 })}
          alt={title ? `${title} - billede ${currentIndex + 1}` : `Projektbillede ${currentIndex + 1}`}
          className="max-h-[82vh] max-w-[88vw] object-contain rounded-lg shadow-2xl transition duration-200 ring-1 ring-white/10"
        />
      </div>

      {/* Navigation arrows (only if multiple images) */}
      {images.length > 1 && (
        <>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              onPrev?.()
            }}
            aria-label="Forrige billede"
            className="absolute left-4 top-1/2 -translate-y-1/2 flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur transition hover:bg-red-600 hover:scale-105 active:scale-95"
          >
            <i className="fa-solid fa-chevron-left text-lg" aria-hidden="true" />
          </button>

          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              onNext?.()
            }}
            aria-label="Næste billede"
            className="absolute right-4 top-1/2 -translate-y-1/2 flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur transition hover:bg-red-600 hover:scale-105 active:scale-95"
          >
            <i className="fa-solid fa-chevron-right text-lg" aria-hidden="true" />
          </button>
        </>
      )}
    </div>
  )
}
